import React from 'react'
import classNames from 'classnames'
import Toggleable from '../../rc-components/Toggleable'
import { DropdownCb } from '../../types/Common'
import { formatDate } from '../../utils/datetime'
import BloodResultItemCard, { BloodResultItem, BloodResultItemStatus } from '../BloodResultItemCard'

import './BloodResult.scss'
import useTranslate from "../../hooks/useTranslate";

const computeBloodResultStatus = (item: BloodResultItem): BloodResultItemStatus => {
  const { value, referenceMin, referenceMax } = item
  if (!Number.isFinite(value)) {
    return 'unknown'
  }

  const hasMin = Number.isFinite(referenceMin)
  const hasMax = Number.isFinite(referenceMax)

  // Both bounds available: use full range comparison
  if (hasMin && hasMax) {
    if (referenceMax < referenceMin) return 'unknown'
    if (value < referenceMin) return 'below_range'
    if (value > referenceMax) return 'out_of_range'
    return 'in_range'
  }

  // One-sided ranges are common (only low OR only high)
  if (hasMin) {
    return value < referenceMin ? 'below_range' : 'in_range'
  }

  if (hasMax) {
    return value > referenceMax ? 'out_of_range' : 'in_range'
  }

  return 'unknown'
}

export type BloodResultProps = {
  title?: string
  labName?: string
  updatedAt?: string | number | Date
  items: BloodResultItem[]
  points?: number
  maxPoints?: number
  expandForPdf?: boolean
}

const BloodResult: React.FC<BloodResultProps> = (props) => {
  const translateLocalazy = useTranslate()

  const { updatedAt, items } = props
  const computedItems = items.map((item) => ({
    ...item,
    status: computeBloodResultStatus(item),
  }))

  const maxPoints = props.maxPoints ?? items.length
  const points = props.points ?? computedItems.reduce((acc, item) => acc + (item.status === 'in_range' ? 1 : 0), 0)

  const totalCount = Math.max(maxPoints, 0)
  const inRangeCount = Math.max(Math.min(points, totalCount), 0)
  const outOfRangeCount = Math.max(totalCount - inRangeCount, 0)

  // Render up to 20 segments; if there are more results, compress into a proportional bar
  const segmentTotal = totalCount <= 20 ? totalCount : 20
  const inRangeSegments =
    segmentTotal === 0
      ? 0
      : totalCount <= 20
        ? inRangeCount
        : Math.round((inRangeCount / totalCount) * segmentTotal)

  return (
    <div className="flex-item item-sm-12 break_inside--avoid_page">
      <div className={classNames('card-box diagnostic_test-box blood-result-box', { 'blood-result-box--pdf': props.expandForPdf })} style={{ overflow: 'visible' }}>
        {props.expandForPdf ? (
          <div>
            <div className="blood-result__header-row">
              <div className="flex-container justify-between align-start cog-test-wrapper">
                <div className="flex-item flex-item--shrink">
                  <div className="test-title">{translateLocalazy('blood-results.title')}</div>
                  {props.labName && <div className="blood-result__lab-name">{props.labName}</div>}
                </div>

                <div className="flex-item flex-item--shrink results-block">
                  <div className="blood-result__summary">
                    <div className="blood-result__summary-title">
                      {translateLocalazy('blood-results.within_reference_range')}
                    </div>

                    <div className="blood-result__summary-value" aria-label={`${inRangeCount} / ${totalCount}`}>
                      <span className="blood-result__summary-value-in">{inRangeCount}</span>
                      <span className="blood-result__summary-value-sep"> / </span>
                      <span className="blood-result__summary-value-total">{totalCount}</span>
                    </div>

                    {segmentTotal > 0 && (
                      <div className="blood-result__summary-bar" aria-hidden="true">
                        {Array.from({ length: segmentTotal }, (_, idx) => (
                          <div
                            // eslint-disable-next-line react/no-array-index-key
                            key={idx}
                            className={[
                              'blood-result__summary-segment',
                              idx < inRangeSegments
                                ? 'blood-result__summary-segment--in'
                                : 'blood-result__summary-segment--out',
                            ].join(' ')}
                          />
                        ))}
                      </div>
                    )}

                    <div className="blood-result__summary-footer">
                      <div className="blood-result__summary-footer-item">
                        <span className="blood-result__summary-footer-label">
                          {translateLocalazy('blood-results.in_range')}
                        </span>
                        <span className="blood-result__summary-footer-value">{inRangeCount}</span>
                      </div>
                      <div className="blood-result__summary-footer-divider" />
                      <div className="blood-result__summary-footer-item">
                        <span className="blood-result__summary-footer-label">
                          {translateLocalazy('blood-results.out_of_range')}
                        </span>
                        <span className="blood-result__summary-footer-value">{outOfRangeCount}</span>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="blood-result__updated-at flex-item flex-item--shrink" style={{ flex: '0 0 220px' }}>
                  {!!updatedAt && (
                    <div className="blood-result__updated-at-text">
                      {translateLocalazy('Last Update')}: {formatDate(updatedAt)}
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className="diagnostic_test_body blood-result__body">
              <div className="blood-result__items blood-result__items--pdf-two-cols">
                {computedItems.map((item) => (
                  <BloodResultItemCard key={item.id} item={item} />
                ))}
              </div>
            </div>
          </div>
        ) : (
          <Toggleable>
            {(dropdownId: string, setDropdownId: DropdownCb): React.ReactElement => (
              <div>
                <div className="blood-result__header-row">
                  <div className="flex-container justify-between align-start cog-test-wrapper"
                       onClick={(): void => setDropdownId(dropdownId ? '' : 'blood')}>
                    <div className="flex-item flex-item--shrink">
                      <div className="test-title">{translateLocalazy('blood-results.title')}</div>
                      {props.labName && <div className="blood-result__lab-name">{props.labName}</div>}
                    </div>

                    <div className="flex-item flex-item--shrink results-block">
                      <div className="blood-result__summary">
                        <div className="blood-result__summary-title">
                          {translateLocalazy('blood-results.within_reference_range')}
                        </div>

                        <div className="blood-result__summary-value" aria-label={`${inRangeCount} / ${totalCount}`}>
                          <span className="blood-result__summary-value-in">{inRangeCount}</span>
                          <span className="blood-result__summary-value-sep"> / </span>
                          <span className="blood-result__summary-value-total">{totalCount}</span>
                        </div>

                        {segmentTotal > 0 && (
                          <div className="blood-result__summary-bar" aria-hidden="true">
                            {Array.from({ length: segmentTotal }, (_, idx) => (
                              <div
                                // eslint-disable-next-line react/no-array-index-key
                                key={idx}
                                className={[
                                  'blood-result__summary-segment',
                                  idx < inRangeSegments
                                    ? 'blood-result__summary-segment--in'
                                    : 'blood-result__summary-segment--out',
                                ].join(' ')}
                              />
                            ))}
                          </div>
                        )}

                        <div className="blood-result__summary-footer">
                          <div className="blood-result__summary-footer-item">
                            <span className="blood-result__summary-footer-label">
                              {translateLocalazy('blood-results.in_range')}
                            </span>
                            <span className="blood-result__summary-footer-value">{inRangeCount}</span>
                          </div>
                          <div className="blood-result__summary-footer-divider" />
                          <div className="blood-result__summary-footer-item">
                            <span className="blood-result__summary-footer-label">
                              {translateLocalazy('blood-results.out_of_range')}
                            </span>
                            <span className="blood-result__summary-footer-value">{outOfRangeCount}</span>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="blood-result__updated-at flex-item flex-item--shrink" style={{ flex: '0 0 220px' }}>
                      {!!updatedAt && (
                        <div className="blood-result__updated-at-text">
                          {translateLocalazy('Last Update')}: {formatDate(updatedAt)}
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {dropdownId && (
                  <div className="diagnostic_test_body blood-result__body">
                    <div className="blood-result__items">
                      {computedItems.map((item) => (
                        <BloodResultItemCard key={item.id} item={item} />
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </Toggleable>
        )}
      </div>
    </div>
  )
}

export default BloodResult


